import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import ShowsSection from "@/components/ShowsSection";
import ContentSubmission from "@/components/ContentSubmission";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import AdminPortal from "@/components/AdminPortal";
import TrailerModal from "@/components/TrailerModal";
import api, { apiViewer, formatApiError } from "@/lib/api";
import AuthModal from "@/components/AuthModal";
import MyWatchlistModal from "@/components/MyWatchlistModal";
import SubscribeModal from "@/components/SubscribeModal";

const VIEWER_TOKEN_KEY = "viewna_viewer_token";

function App() {
  const [shows, setShows] = useState([]);
  const [query, setQuery] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);
  const [trailerShow, setTrailerShow] = useState(null);
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(null);
  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [watchlist, setWatchlist] = useState(new Set());
  const [liked, setLiked] = useState(new Set());

  const loadShows = useCallback(
    () => api.get("/shows").then((r) => setShows(r.data)).catch(() => toast.error("Could not load shows")),
    []
  );

  useEffect(() => {
    loadShows();
  }, [loadShows]);

  useEffect(() => {
    if (!localStorage.getItem(VIEWER_TOKEN_KEY)) return;
    apiViewer
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        return apiViewer.get("/watchlist");
      })
      .then((r) => setWatchlist(new Set(r.data.slugs)))
      .then(() => apiViewer.get("/likes"))
      .then((r) => setLiked(new Set(r.data.slugs)))
      .catch(() => localStorage.removeItem(VIEWER_TOKEN_KEY));
  }, []);

  const hero = shows.find((s) => s.is_launch) || shows[0] || null;

  const toggleWatchlist = (slug, title) => {
    if (!user) {
      requireAuth("Sign in to add shows to your watchlist");
      return;
    }
    apiViewer
      .post("/watchlist/toggle", { slug })
      .then(({ data }) => {
        setWatchlist(new Set(data.slugs));
        if (data.added) toast.success(`${title} added to your Watchlist`);
        else toast(`${title} removed from your Watchlist`);
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };

  const handleAuthed = (u, token) => {
    if (u.role === "admin") {
      localStorage.removeItem(VIEWER_TOKEN_KEY);
      localStorage.setItem("viewna_admin_token", token || "");
      setAuthOpen(null);
      setAdminOpen(true);
      toast.success("Admin verified — opening the control room");
      return;
    }
    setUser(u);
    setAuthOpen(null);
    apiViewer.get("/watchlist").then((r) => setWatchlist(new Set(r.data.slugs))).catch(() => {});
    apiViewer.get("/likes").then((r) => setLiked(new Set(r.data.slugs))).catch(() => {});
  };

  const requireAuth = (message) => {
    setAuthOpen("signin");
    toast.info(message);
  };

  const handleSubscribed = () => {
    setSubscribeOpen(false);
    if (localStorage.getItem(VIEWER_TOKEN_KEY)) {
      apiViewer.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
    }
    toast.success("Welcome to VIEWNA Premium — ads removed, all shows unlocked!");
  };

  const openTrailer = (show) => {
    if (!user) {
      requireAuth("Sign in to play and watch");
      return;
    }
    setTrailerShow(show);
  };

  const toggleLike = (slug, title) => {
    if (!user) {
      requireAuth("Sign in to like shows");
      return;
    }
    apiViewer
      .post("/likes/toggle", { slug })
      .then(({ data }) => {
        setLiked(new Set(data.slugs));
        if (data.added) toast.success(`You liked ${title}`);
        else toast(`Like removed from ${title}`);
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };

  const signOut = () => {
    localStorage.removeItem(VIEWER_TOKEN_KEY);
    setUser(null);
    setWatchlist(new Set());
    setLiked(new Set());
    toast("Signed out of VIEWNA");
  };

  const filtered = query.trim()
    ? shows.filter((s) =>
        `${s.title} ${s.category} ${(s.badges || []).join(" ")}`.toLowerCase().includes(query.trim().toLowerCase())
      )
    : shows;

  return (
    <div className="min-h-screen overflow-x-clip bg-ink-950 font-body text-stone-100">
      <Header
        query={query}
        onQueryChange={setQuery}
        user={user}
        onOpenAuth={() => setAuthOpen("signin")}
        onOpenWatchlist={() => setWatchlistOpen(true)}
        onOpenSubscribe={() => setSubscribeOpen(true)}
        onSignOut={signOut}
      />
      <main>
        <Hero show={hero} onTrailer={openTrailer} watchlist={watchlist} onWatchlist={toggleWatchlist} liked={liked} onLike={toggleLike} />
        <ShowsSection
          shows={filtered}
          allCount={shows.length}
          query={query}
          onTrailer={openTrailer}
          onClearQuery={() => setQuery("")}
          liked={liked}
          onLike={toggleLike}
        />
        <ContentSubmission />
        <ContactSection />
      </main>
      <Footer onOpenAdmin={() => setAdminOpen(true)} />
      {trailerShow && (
        <TrailerModal
          show={trailerShow}
          onClose={() => setTrailerShow(null)}
          isPremiumUser={!!(user && user.is_premium)}
          onUpgrade={() => { setTrailerShow(null); setSubscribeOpen(true); }}
        />
      )}
      {adminOpen && <AdminPortal onClose={() => setAdminOpen(false)} onChanged={loadShows} />}
      {authOpen && (
        <AuthModal initialMode={authOpen} onClose={() => setAuthOpen(null)} onAuthed={handleAuthed} />
      )}
      {watchlistOpen && (
        <MyWatchlistModal
          onClose={() => setWatchlistOpen(false)}
          onTrailer={(show) => { setWatchlistOpen(false); setTrailerShow(show); }}
          onChanged={(set) => setWatchlist(set)}
        />
      )}
      {subscribeOpen && (
        <SubscribeModal
          user={user}
          onClose={() => setSubscribeOpen(false)}
          onSubscribed={handleSubscribed}
          requireAuth={requireAuth}
        />
      )}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#141110",
            border: "1px solid rgba(212,175,55,0.35)",
            color: "#f5efe0",
          },
        }}
      />
    </div>
  );
}

export default App;
